import { CircleAlertIcon } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button.js';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover.js';

export function GenerationDraftWarnings({
  cases,
  additionalWarnings = [],
  message,
  truncatedCount = 0,
  contextPartial = false,
}: {
  cases: ReadonlyArray<{ generationWarnings?: string[] }>;
  additionalWarnings?: readonly string[];
  message?: string | null;
  truncatedCount?: number;
  contextPartial?: boolean;
}) {
  const [visibleCount, setVisibleCount] = useState(10);
  const warnings = [
    ...new Set(
      [
        ...cases.flatMap((testCase) => testCase.generationWarnings ?? []),
        ...additionalWarnings,
        ...(message &&
        !message.startsWith('Some generated cases need attention.')
          ? [message]
          : []),
      ]
        .filter((warning) => typeof warning === 'string' && warning.trim())
        .map((warning) => warning.trim().slice(0, 500)),
    ),
  ];
  if (!warnings.length && truncatedCount <= 0 && !contextPartial) return null;
  return (
    <Popover
      onOpenChange={(open) => {
        if (!open) setVisibleCount(10);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 text-amber-600 hover:text-amber-700"
          aria-label="Generation notes"
        >
          <CircleAlertIcon className="size-4" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 max-w-[calc(100vw-2rem)] p-3 text-xs"
      >
        <p className="font-medium">Generation notes</p>
        {truncatedCount > 0 ? (
          <p className="mt-2">
            Context is truncated: some source content was not retained in full.
          </p>
        ) : contextPartial ? (
          <p className="mt-2">
            Only part of the selected source context is available.
          </p>
        ) : null}
        {warnings.length > 0 ? (
          <ul className="mt-2 max-h-64 space-y-2 overflow-auto break-words">
            {warnings.slice(0, visibleCount).map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        ) : null}
        {warnings.length > visibleCount ? (
          <Button
            variant="ghost"
            size="sm"
            className="mt-2"
            onClick={() => setVisibleCount((count) => count + 10)}
          >
            Show more notes ({warnings.length - visibleCount} remaining)
          </Button>
        ) : null}
        <p className="mt-2 text-muted-foreground">
          You can still add these cases.
        </p>
      </PopoverContent>
    </Popover>
  );
}
